import { ErrorCatcher } from '../../../tools/error-catcher';
import { Match, Owner, Show, Switch, createEffect, createSignal, getOwner, lazy, runWithOwner, useContext } from 'solid-js';
import { EntitySlug, ValidDataTypes, pdfStore, setPdfStore, supportedTypes } from '../pdf-store';
import { Portal, isServer } from 'solid-js/web';
import { PrintCviIcon } from '../../icons-library/print-cvi';
import { DocumentCviIcon } from '../../icons-library/document-cvi';
import { AppContext } from '../../../app-context-provider/app-context-provider';
import { breakpoints } from '../../../style/theme';
import { LoadingSpinner } from '../../loading-spinner/loading-spinner';
import { incrementUserPdfPrintCount } from '../pdf-dashboard/pdf-newsletter-signup-prompt/newsletter-prompt-checker';
import { useLocation, useNavigate } from '@solidjs/router';
import { linkItemClass } from '../../share-and-save/share-and-save';

/* Lazy, like app.tsx and the dashboard: share-and-save → this file → pdf-factory →
   extract-blocks → dynamic-module-generator → … → share-and-save is a static cycle that
   Vite dev SSR trips over after an HMR invalidation burst. */
const PdfFactory = lazy(() => import('../pdf-factory/pdf-factory').then((imported) => ({ default: imported.PdfFactory })));


export const PdfToolbar = (props: { type: EntitySlug; data: ValidDataTypes }) => {
    const { 
        localize,
        viewport,
        siteInfo,
        appStore,
        ...rest
    } = useContext(AppContext);

    const [triggeredViaButton, setTriggeredViaButton] = createSignal(false);

    const location = useLocation();
    const navigate = useNavigate();

    const owner = getOwner();

    if (!isServer) {
        /**
         * This allows the user to get the print-rendered content via File->Print or Ctrl/CMD + P.
         * The extra logic ensures that the print will not trigger endlessly if the user
         * prints via the dedicated "Print PDF" button.
         */
        window.onbeforeprint = function() {
            if (triggeredViaButton()) return;
            preparePrint(false);
        };

        window.onafterprint = function() {
            setTriggeredViaButton(false);
        };
    }

    const preparePrint = (viaButton: boolean) => {
        setTriggeredViaButton(viaButton);

        if (pdfStore.status === 'printing') {
            return;
        }

        setPdfStore('renderComplete', false);
        setPdfStore('status', 'printing');
        setPdfStore('title', `${props.data.post_title} - ${siteInfo.siteType === 'atos-care' ? 'Atos Care' : 'Atos Medical'}`);
        setPdfStore('postsData', [{ type: props.type, data: props.data, key: ''}]);

        incrementUserPdfPrintCount();
    };

    createEffect(() => {
        if (pdfStore.status === 'printing' && pdfStore.renderComplete) {
            setPdfStore('status', '');
            window.print();
        }
    });

    const isMobile = () => viewport.width <= breakpoints.MOBILE;

    const excludedEndpointsForToolbarList = () => {
        const endpoints = [
            rest.eventsListingPage?.url,
            rest.hcpEventsListingPage?.url,
            rest.blogsNewsListingPage?.url,
            rest.hcpBlogsNewsListingPage?.url,
            rest.videosListingPage?.url,
            rest.pdfBuilderPage,
            rest.newsletterSignupPage,
            rest.pageRequestPasswordResetUrl,
            rest.pageResetPasswordUrl,
            rest.pageVerifyAccountUrl,
            rest.shopPage?.url,
            rest.page404?.url,
            rest.pageRegistration?.url,
            rest.searchPage?.url,
            rest.productContactFormUrl,
            rest.coursesListingPage?.url,
            // Probably don't need these here since these are hard-coded (i.e. not actual CMS pages)
            rest.userProfileUrls.account,
            rest.userProfileUrls.bookmarks,
            rest.userProfileUrls.learning,
            rest.userProfileUrls.orders,
        ].filter(endpoint => endpoint !== undefined && endpoint !== '');
    
        return endpoints as string[];
    };

    const supported = () => {
        if (excludedEndpointsForToolbarList().includes(location.pathname)) {
            return false;
        }
        
        return supportedTypes.includes(props.type) ;
    };

    return (
        <ErrorCatcher componentName='Pdf Toolbar'>

            <Show when={appStore.isHcp() && rest.pdfBuilderPage.url}>
                <button
                    class={linkItemClass}
                    onClick={() => navigate(rest.pdfBuilderPage.url)}
                    aria-label={localize('navigate-to-pdf-builder', 'Navigate to PDF Builder')}
                >
                    <DocumentCviIcon size="lg" ariaLabel={rest.pdfBuilderPage.title} />
                    <span class="body-md font-cvi-regular">
                        {rest.pdfBuilderPage.title}
                    </span>
                </button>
            </Show>

            <Show when={supported() && !isMobile()}>
                <button class={linkItemClass} onClick={() => preparePrint(true)} id='print-pdf-button'>
                    <Switch>
                        <Match when={pdfStore.status !== 'printing'}>
                            <PrintCviIcon size="lg" ariaLabel={localize('print-pdf', 'Print PDF')} />
                        </Match>

                        <Match when={pdfStore.status === 'printing'}>
                            <LoadingSpinner size={'2.2rem'} />
                        </Match>
                    </Switch>
                    <span class="body-md font-cvi-regular">
                        {localize('print-pdf', 'Print PDF')}
                    </span>
                </button>

                <Portal mount={document.getElementById('pdf-factory-root')!}>
                    {runWithOwner(owner as Owner, () => (
                        <PdfFactory />
                    ))}
                </Portal>

            </Show>
        </ErrorCatcher>
    );
};